import {
  phone_regex,
  zipcode_regex,
} from '@tcs/client-portal-common/utils/regularExpressions';
import { STATES } from '@tcs/common/assets/constants';
import { TransferMethod } from '@tcs/common/types/TransferMethod';
import { TransferType } from '@tcs/common/types/TransferType';
import { boolean, lazy, number, object, string } from 'yup';

export const TRANSFERS_IN_ERRORS = {
  selectedAccount: {
    required: 'Please select an account',
  },
  transferFromAccount: {
    required: 'Account number is required',
    max: 'Account number must be 50 characters or less',
  },
  accountType: {
    required: 'Account type is required',
  },
  iraProvider: {
    required: 'IRA provider is required',
    max: 'IRA provider must be 50 characters or less',
  },
  address: {
    required: 'Street address is required',
    max: 'Street address must be 30 characters or less',
  },
  city: {
    required: 'City is required',
    max: 'City must be 21 characters or less',
  },
  state: {
    required: 'State is required',
    invalid: 'Please select a valid state',
  },
  zipCode: {
    required: 'Zip code is required',
    invalid: 'Please enter a valid zip code',
    min5: 'Zip code must be at least 5 digits',
    min9: 'Zip code must be 10 characters when using extended format',
    max: 'Zip code cannot exceed 10 characters',
  },
  phone: {
    required: 'Phone number is required',
    invalid: 'Please enter a valid phone number',
  },
  transferAmount: {
    required: 'Transfer amount is required when exact amount is selected',
    min: 'Transfer amount must be greater than 0',
  },
};

export const TransfersInCashFormSchema = (errorsMap = TRANSFERS_IN_ERRORS) =>
  object({
    selectedAccount: string().required(errorsMap.selectedAccount.required),
    transferFromAccount: string()
      .required(errorsMap.transferFromAccount.required)
      .max(50, errorsMap.transferFromAccount.max),
    accountType: string().required(errorsMap.accountType.required),
    isInheritedAccount: boolean(),
    iraProvider: string()
      .required(errorsMap.iraProvider.required)
      .max(50, errorsMap.iraProvider.max),
    address: string()
      .required(errorsMap.address.required)
      .max(30, errorsMap.address.max),
    city: string()
      .required(errorsMap.city.required)
      .max(21, errorsMap.city.max),
    state: string()
      .required(errorsMap.state.required)
      .oneOf(
        STATES.map(({ Id }) => Id),
        errorsMap.state.invalid,
      ),
    zipCode: lazy((value) => {
      if (!value || value.length <= 0) {
        return string().required(errorsMap.zipCode.required);
      }

      if (value?.length > 5) {
        return string()
          .matches(zipcode_regex, errorsMap.zipCode.invalid)
          .min(10, errorsMap.zipCode.min9)
          .max(10, errorsMap.zipCode.max);
      }

      return string()
        .matches(zipcode_regex, errorsMap.zipCode.invalid)
        .min(5, errorsMap.zipCode.min5);
    }),
    phone: string()
      .required(errorsMap.phone.required)
      .matches(phone_regex, errorsMap.phone.invalid),
    overnightDeliveryCostFedexAccountNumber: string()
      .max(16, 'Account number must not be longer than 16 characters')
      .nullable()
      .optional(),
    transferType: string().required(),
    transferMethod: string().when('transferType', {
      is: (transferType) => transferType === TransferType.Partial,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    transferAmount: number().when(['transferType', 'transferMethod'], {
      is: (transferType, transferMethod) =>
        transferType === TransferType.Partial &&
        transferMethod === TransferMethod.ExactAmount,
      then: (schema) =>
        schema
          .required(errorsMap.transferAmount.required)
          .min(0.01, errorsMap.transferAmount.min),
      otherwise: (schema) => schema.optional(),
    }),
  });
